// useUpdateOrderStatusMutation.js
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { addPaymentToRepair } from "api/repair-orders";

const useAddPayment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(addPaymentToRepair, {
    onMutate: async ({
      repairOrderId,
      amount,
      paymentMethod,
      isDeposit,
      paymentType,
    }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["repairOrder", repairOrderId],
      });
      // Snapshot the previous data
      const previousOrder = queryClient.getQueryData([
        "repairOrder",
        repairOrderId,
      ]);
      if (!previousOrder) {
        console.error("No previous order found");
        console.log("repairOrderId", repairOrderId);
        return;
      }

      const newPayment = {
        id: `temp-${Date.now()}`,
        amount,
        paymentMethod,
        isDeposit,
        paymentType,
        createdAt: dayjs().toISOString(),
        updatedAt: dayjs().toISOString(),
      };

      // Optimistically update the repair order with the new comment
      queryClient.setQueryData(["repairOrder", repairOrderId], (old) => ({
        ...old,
        updatedAt: dayjs().toISOString(),
        repairPayments: [...(old.repairPayments || []), newPayment],
      }));

      // Return context with the previous order for rollback
      return { previousOrder };
    },
    onError: (error, variables, context) => {
      if (context?.previousOrder) {
        queryClient.setQueryData(
          ["repairOrder", variables.repairOrderId],
          context.previousOrder
        );
      }
      console.error(error?.response?.data?.message || "Error adding payment");
      enqueueSnackbar("Failed to add payment", { variant: "error" });
    },
    onSuccess: (data, variables) => {
      enqueueSnackbar("Payment added successfully", { variant: "success" });
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries(["repairOrder", variables.repairOrderId]);
    },
  });
};

export default useAddPayment;
